/**
 * Hook di formattazione centralizzato (DEC-028 + DEC-029).
 *
 * Legge locale e valuta dalla configurazione globale del tenant
 * e restituisce funzioni di formattazione pronte all'uso.
 * I moduli non devono mai usare 'it-IT' o 'EUR' direttamente.
 */

import { useMemo } from 'react';
import { useConfig } from '../shell/ConfigContext';

interface Formatters {
  /** Formatta un importo in valuta (es. "€ 12,50"). */
  currency: (value: number | string) => string;
  /** Formatta una data ISO in formato breve (es. "13/03/2026"). */
  date: (iso: string) => string;
  /** Formatta un timestamp ISO in data + ora (es. "13/03/2026 14:30"). */
  dateTime: (iso: string) => string;
  /** Formatta un orario ISO in formato breve (es. "14:30"). */
  time: (iso: string) => string;
  /** Formatta una data in formato esteso (es. "giovedì 13 marzo 2026"). */
  extendedDate: (dateOrIso: Date | string) => string;
  /** Formatta un numero con i separatori del locale. */
  number: (value: number | string, decimals?: number) => string;
  /** Formatta una quantità: max 3 decimali, zero-trailing rimossi (2 → "2", 2.5 → "2,5"). */
  qty: (value: number | string) => string;
}

/**
 * Hook che restituisce i formatter legati alla configurazione del tenant.
 *
 * @returns Oggetto Formatters con tutte le funzioni di formattazione.
 */
export function useFormatters(): Formatters {
  const { config } = useConfig();
  const locale = config.locale ?? 'it-IT';
  const valuta = config.valuta ?? 'EUR';
  const decimaliValuta = parseInt(config.decimali_valuta ?? '2', 10);

  return useMemo<Formatters>(() => ({
    currency(value: number | string): string {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: valuta,
        minimumFractionDigits: decimaliValuta,
        maximumFractionDigits: decimaliValuta,
      }).format(Number(value));
    },

    date(iso: string): string {
      return new Date(iso).toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    dateTime(iso: string): string {
      return new Date(iso).toLocaleString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    time(iso: string): string {
      return new Date(iso).toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    extendedDate(dateOrIso: Date | string): string {
      const d = typeof dateOrIso === 'string' ? new Date(dateOrIso) : dateOrIso;
      return d.toLocaleDateString(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    },

    number(value: number | string, decimals?: number): string {
      const opts: Intl.NumberFormatOptions = {};
      if (decimals !== undefined) {
        opts.minimumFractionDigits = decimals;
        opts.maximumFractionDigits = decimals;
      }
      return new Intl.NumberFormat(locale, opts).format(Number(value));
    },

    qty(value: number | string): string {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      }).format(Number(value));
    },
  }), [locale, valuta, decimaliValuta]);
}
